import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { Clipboard } from '@angular/cdk/clipboard';
import { computed, effect, inject, Injector, Signal, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { TopicOrder } from '@models/marketing/topics/topics.model';
import { NewsArticleGroup } from '@models/news-article-group';
import {
  CreateLocalizedArticleRequest,
  CreateNewsArticleRequest,
  NewsArticle,
  NewsArticleComment,
  NewsArticleCommunity,
  NewsArticleCopyRequest,
  NewsArticleListItem,
  NewsArticleStatus,
  UpdateNewsArticleRequest,
} from '@models/news-article/news-article.model';
import { patchState, signalStore, StateSignals, withComputed, withHooks, withMethods, withState, WritableStateSource } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { FileStorageService, NotificationsService } from 'cui-components';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { AppConfigService } from 'src/app/infrastructure';
import {
  createCustomStateSetOnRegexHandler,
  createCustomStateSetOnUrlHandler,
  deleteEntity,
  getAll,
  getOneWithParams,
  guidRegex,
  StateDictionary,
} from '../helpers';
import { findAndReplace, sortAlphabetically } from '../helpers/helper';
import { NewsArticleGroupsStore } from '../news-article-groups/news-article-groups.store';
import { getNewsArticleCommentMethods, NewsArticleCommentsStoreType } from './news-article-store-partials/news-article-comments';
import {
  getNewsArticleCommunityHandlingMethods,
  NewsArticleCommunityHandlingStoreType,
} from './news-article-store-partials/news-article-community-handling';
import {
  getNewsArticleConfigurationHandlingMethods,
  NewsArticleConfigurationHandlingStoreType,
} from './news-article-store-partials/news-article-configuration-handling';
import {
  getNewsArticleFieldExpertsMethods,
  NewsArticleFieldExpertsStoreType,
} from './news-article-store-partials/news-article-field-experts';
import {
  getNewsArticlePromotionHandlingMethods,
  NewsArticlePromotionHandlingStoreType,
} from './news-article-store-partials/news-article-promotions';
import { getNewsArticlesTopicsOrderMethods, NewsArticleTopicsOrderStoreType } from './news-article-store-partials/news-article-topic-order';
import { GetNewsArticlesParams, NewsArticlesService } from './news-articles.service';

export const ToggleOptions = ['Active', 'All'] as const;
export type ToggleOptionsName = (typeof ToggleOptions)[number];

type NewsArticlesState = {
  toggle: ToggleOptionsName;
  newsArticles: NewsArticleListItem[];
  selectedNewsArticle: NewsArticle | null;
  isLoading: boolean;
  isSelectedNewsArticleLoading: boolean;
  isNewsArticleCreateLoading: boolean;

  newsArticleComments: NewsArticleComment[];
  isNewsArticleCommentsLoading: boolean;

  selectedNewsArticleCommunityId: string | null;

  newsArticlesByCommunity: NewsArticleListItem[];
  isNewsArticlesByCommunityLoading: boolean;

  isFieldExpertsLoading: boolean;

  topicsOrder: TopicOrder[];
  isTopicsOrderLoading: boolean;
  isSetTopicsOrderLoading: boolean;
};

const initialState: NewsArticlesState = {
  newsArticles: [],
  toggle: 'All',
  selectedNewsArticle: null,
  isLoading: false,
  isSelectedNewsArticleLoading: false,
  isNewsArticleCreateLoading: false,

  newsArticleComments: [],
  isNewsArticleCommentsLoading: false,

  selectedNewsArticleCommunityId: null,

  newsArticlesByCommunity: [],
  isNewsArticlesByCommunityLoading: false,

  isFieldExpertsLoading: false,

  topicsOrder: [],
  isTopicsOrderLoading: false,
  isSetTopicsOrderLoading: false,
};

const newsArticlesDictionary: StateDictionary<NewsArticleListItem> = {
  entityList: 'newsArticles',
  listLoading: 'isLoading',
  createLoading: 'isNewsArticleCreateLoading',
  selectedEntity: 'selectedNewsArticle',
  selectedEntityLoading: 'isSelectedNewsArticleLoading',
  entityId: 'id',
  sortFn: sortNewsByDateTime,
};

const newsArticlesByCommunityDictionary: StateDictionary<NewsArticleListItem> = {
  ...newsArticlesDictionary,
  entityList: 'newsArticlesByCommunity',
  listLoading: 'isNewsArticlesByCommunityLoading',
};

export const NewsArticlesStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('news-articles-store'),
  withComputed(store => ({
    selectedNewsArticleId: computed(() => store.selectedNewsArticle()?.id || null),
    selectedNewsArticleCommunity: computed(() => {
      const article = store.selectedNewsArticle();
      const communityId = store.selectedNewsArticleCommunityId();
      if (article && article.communities?.length) {
        return article.communities.find(item => item.id === communityId) ?? null;
      }
      return null;
    }),
    activeNewsArticles: computed(() => store.newsArticles().filter(item => item.status === 'Active')),
  })),

  withMethods(
    (
      store,
      newsArticlesService = inject(NewsArticlesService),
      router = inject(Router),
      appConfig = inject(AppConfigService),
      notifications = inject(NotificationsService),
      clipboard = inject(Clipboard),
      fileStorageService = inject(FileStorageService),
      notificationsService = inject(NotificationsService),
      injector = inject(Injector),
    ) => {
      createCustomStateSetOnUrlHandler<NewsArticlesState>('/marketing/news-articles', {
        selectedNewsArticle: null,
        newsArticleComments: [],
        selectedNewsArticleCommunityId: null,
      })(store);
      createCustomStateSetOnRegexHandler<NewsArticlesState>(new RegExp(`^/marketing/communities/${guidRegex}$`))(store, {
        newsArticlesByCommunity: [],
      });
      createCustomStateSetOnRegexHandler<NewsArticlesState>(new RegExp(`^/marketing/communities/${guidRegex}/news-articles$`))(store, {
        selectedNewsArticle: null,
      });
      createCustomStateSetOnRegexHandler<NewsArticlesState>(new RegExp(`^/marketing/news-articles/article/${guidRegex}/communities$`))(
        store,
        {
          selectedNewsArticleCommunityId: null,
        },
      );
      createCustomStateSetOnUrlHandler<NewsArticlesState>('/marketing/communities', { newsArticlesByCommunity: [] })(store);

      const contextInjections: {
        store: NewsArticlesStore;
        newsArticlesService: NewsArticlesService;
        router: Router;
      } = {
        store,
        newsArticlesService,
        router,
      };
      const storeBaseMethodOptions = {
        store,
        service: newsArticlesService,
        dictionary: newsArticlesDictionary,
      };

      const newsArticlesCrudMethods = {
        getAllNewsArticles: getAll<NewsArticleListItem, GetNewsArticlesParams>(storeBaseMethodOptions),

        getNewsArticle: getOneWithParams<NewsArticle, { id: string; includeContent: boolean }>({
          ...storeBaseMethodOptions,
          service: {
            getOne: ({ id, includeContent }) => newsArticlesService.getOne(id, includeContent),
          },
          onErrorCallback: () => {
            const currentUrl = router.url;
            const withoutLastSegment = currentUrl.split('/').slice(0, -1);
            router.navigate(withoutLastSegment);
          },
        }),

        getAllNewsArticlesByCommunity: getAll<NewsArticleListItem, GetNewsArticlesParams>({
          ...storeBaseMethodOptions,
          dictionary: newsArticlesByCommunityDictionary,
        }),

        addNewsArticle: rxMethod<{ newsArticle: CreateNewsArticleRequest; communityId?: string }>(
          pipe(
            tap(() =>
              patchState(store, {
                isNewsArticleCreateLoading: true,
                isSelectedNewsArticleLoading: true,
                isNewsArticlesByCommunityLoading: true,
                isLoading: true,
              }),
            ),
            switchMap(({ newsArticle, communityId }) =>
              newsArticlesService.create(newsArticle).pipe(
                tap(newArticle => {
                  patchState(store, {
                    selectedNewsArticle: { ...newArticle, content: null },
                    newsArticles: sortAlphabetically([...store.newsArticles(), newArticle], sortNewsByDateTime),
                    newsArticlesByCommunity: communityId
                      ? sortAlphabetically([...store.newsArticlesByCommunity(), newArticle], sortNewsByDateTime)
                      : [],
                    isNewsArticlesByCommunityLoading: false,
                    isSelectedNewsArticleLoading: false,
                    isNewsArticleCreateLoading: false,
                    isLoading: false,
                  });
                  if (communityId) {
                    router.navigate(['marketing', 'communities', communityId, 'news-articles', 'article', newArticle.id, 'details']);
                  } else {
                    router.navigate(['marketing', 'news-articles', 'article', newArticle.id, 'details']);
                  }
                }),
                catchError(() => {
                  patchState(store, {
                    isSelectedNewsArticleLoading: false,
                    isNewsArticlesByCommunityLoading: false,
                    isNewsArticleCreateLoading: false,
                    isLoading: false,
                  });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        updateNewsArticle: rxMethod<{ id: string; newsArticle: UpdateNewsArticleRequest; oldFileId?: string | null }>(
          pipe(
            tap(() =>
              patchState(store, {
                isSelectedNewsArticleLoading: true,
                isLoading: true,
                isNewsArticlesByCommunityLoading: true,
              }),
            ),
            switchMap(({ id, newsArticle, oldFileId }) =>
              newsArticlesService.update(id, newsArticle).pipe(
                tap(updated => {
                  patchState(store, {
                    selectedNewsArticle: { content: store.selectedNewsArticle()?.content ?? null, ...updated },
                    newsArticles: findAndReplaceByIdWithSort(store.newsArticles(), updated),
                    newsArticlesByCommunity: findAndReplaceByIdWithSort(store.newsArticlesByCommunity(), updated),
                  });
                  if (oldFileId) {
                    newsArticlesCrudMethods.deleteFile({ id: oldFileId });
                    return;
                  }
                  patchState(store, {
                    isSelectedNewsArticleLoading: false,
                    isLoading: false,
                    isNewsArticlesByCommunityLoading: false,
                  });
                }),
                catchError(() => {
                  patchState(store, {
                    isSelectedNewsArticleLoading: false,
                    isLoading: false,
                    isNewsArticlesByCommunityLoading: false,
                  });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        activateFileAndUpdateNewsArticle: rxMethod<{
          file: { newId: string; oldId: string | null };
          entity: { id: string; dto: UpdateNewsArticleRequest };
        }>(
          pipe(
            tap(() => patchState(store, { isSelectedNewsArticleLoading: true, isLoading: true, isNewsArticlesByCommunityLoading: true })),
            switchMap(({ file, entity }) =>
              fileStorageService.activateFile(file.newId).pipe(
                tap(() => {
                  newsArticlesCrudMethods.updateNewsArticle({ id: entity.id, newsArticle: entity.dto, oldFileId: file.oldId });
                }),
                catchError(() => {
                  notificationsService.showNotification('Failed to upload file, please try again.');
                  patchState(store, { isSelectedNewsArticleLoading: false, isLoading: false, isNewsArticlesByCommunityLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        deleteFile: rxMethod<{ id: string }>(
          pipe(
            tap(() => patchState(store, { isSelectedNewsArticleLoading: true, isLoading: true, isNewsArticlesByCommunityLoading: true })),
            switchMap(({ id }) =>
              fileStorageService.deleteFile(id).pipe(
                tap(() => {
                  patchState(store, {
                    isSelectedNewsArticleLoading: false,
                    isLoading: false,
                    isNewsArticlesByCommunityLoading: false,
                  });
                }),
                catchError(() => {
                  patchState(store, {
                    isSelectedNewsArticleLoading: false,
                    isLoading: false,
                    isNewsArticlesByCommunityLoading: false,
                  });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        deleteNewsArticle: deleteEntity<NewsArticle>({
          ...storeBaseMethodOptions,
          onSuccessCallback: () => {
            newsArticlesCrudMethods.getAllNewsArticles({ ...getStatusParamFromToggle(store.toggle()) });
            router.navigate(['marketing', 'news-articles']);
          },
        }),

        changeNewsArticleGroup: rxMethod<{ articleId: string; groupId: string | null }>(
          pipe(
            tap(({ groupId }) => {
              patchState(store, { isSelectedNewsArticleLoading: true, isNewsArticleCreateLoading: groupId === null });
            }),
            switchMap(({ articleId, groupId }) =>
              newsArticlesService.changeGroup(articleId, groupId).pipe(
                tap(updatedArticle => {
                  patchState(store, {
                    selectedNewsArticle: { content: store.selectedNewsArticle()?.content ?? null, ...updatedArticle },
                    newsArticles: findAndReplaceByIdWithSort(store.newsArticles(), updatedArticle),
                    newsArticlesByCommunity: findAndReplaceByIdWithSort(store.newsArticlesByCommunity(), updatedArticle),
                    isSelectedNewsArticleLoading: false,
                    isNewsArticleCreateLoading: false,
                  });
                }),
                catchError(() => {
                  patchState(store, { isSelectedNewsArticleLoading: false, isNewsArticleCreateLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        createLocalizedVersion: rxMethod<{ articleId: string; req: CreateLocalizedArticleRequest }>(
          pipe(
            tap(() => patchState(store, { isSelectedNewsArticleLoading: true, isLoading: true })),
            switchMap(({ articleId, req }) =>
              newsArticlesService.createLocalizedVersion(articleId, req).pipe(
                tap(newArticle => {
                  patchState(store, {
                    selectedNewsArticle: newArticle,
                    newsArticles: sortAlphabetically([...store.newsArticles(), newArticle], sortNewsByDateTime),
                    newsArticlesByCommunity: sortAlphabetically([...store.newsArticlesByCommunity(), newArticle], sortNewsByDateTime),
                    isSelectedNewsArticleLoading: false,
                    isLoading: false,
                  });
                  req.communityId
                    ? router.navigate(['marketing', 'communities', req.communityId, 'news-articles', 'article', newArticle.id, 'details'])
                    : router.navigate(['marketing', 'news-articles', 'article', newArticle.id, 'details']);
                }),
                catchError(() => {
                  patchState(store, { isSelectedNewsArticleLoading: false, isLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),
        modifyGroupInfoForArticlesInGroup: (group: NewsArticleGroup) => {
          patchState(store, {
            newsArticles: store
              .newsArticles()
              .map(article =>
                article.groupId === group.id
                  ? { ...article, groupName: group.name, topicId: group.topicId, topicName: group.topicName }
                  : article,
              ),
            newsArticlesByCommunity: store
              .newsArticlesByCommunity()
              .map(article =>
                article.groupId === group.id
                  ? { ...article, groupName: group.name, topicId: group.topicId, topicName: group.topicName }
                  : article,
              ),
          });
        },

        duplicateNewsArticle: rxMethod<{ articleId: string; req: NewsArticleCopyRequest; communityId?: string }>(
          pipe(
            tap(() =>
              patchState(store, {
                isSelectedNewsArticleLoading: true,
                isLoading: true,
              }),
            ),
            switchMap(({ articleId, req, communityId }) =>
              newsArticlesService.copy(articleId, req, communityId).pipe(
                tap(newArticle => {
                  patchState(store, {
                    selectedNewsArticle: newArticle,
                    isSelectedNewsArticleLoading: false,
                    isLoading: false,
                    newsArticles: sortAlphabetically([...store.newsArticles(), newArticle], sortNewsByDateTime),
                    newsArticlesByCommunity: communityId
                      ? sortAlphabetically([...store.newsArticlesByCommunity(), newArticle], sortNewsByDateTime)
                      : store.newsArticlesByCommunity(),
                  });
                  if (communityId) {
                    router.navigate(['marketing', 'communities', communityId, 'news-articles', 'article', newArticle.id, 'details']);
                  } else {
                    router.navigate(['marketing', 'news-articles', 'article', newArticle.id, 'details']);
                  }
                }),
                catchError(() => {
                  patchState(store, {
                    isSelectedNewsArticleLoading: false,
                    isLoading: false,
                  });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),
      };

      const articleConfigMethods = getNewsArticleConfigurationHandlingMethods(contextInjections);

      const articleFieldExpertsMethods = getNewsArticleFieldExpertsMethods(contextInjections);

      const articleCommentsMethods = getNewsArticleCommentMethods(contextInjections);

      const articleCommunitiesMethods = getNewsArticleCommunityHandlingMethods(contextInjections);

      const articlePromotionMethods = getNewsArticlePromotionHandlingMethods(contextInjections);

      const articleTopicsOrderMethods = getNewsArticlesTopicsOrderMethods({
        store,
        newsArticlesService,
      });

      const utilMethods = {
        copyArticleExternalLink: (groupId: string): void => {
          clipboard.copy(`${appConfig.config['FRONTEND_PORTAL_URL']}/news-articles/${groupId}`);
          notifications.showNotification('Copied successfully!');
        },

        resetState: (state: Partial<NewsArticlesState>) => patchState(store, state),
      };

      const navigationActions = {
        goToNewsArticleActions: async (id: string, communityId?: string): Promise<boolean> => {
          const result = communityId
            ? await router.navigate(['marketing', 'communities', communityId, 'news-articles', 'article', id])
            : await router.navigate(['marketing', 'news-articles', 'article', id]);
          if (!result) return false;

          if (!store.isSelectedNewsArticleLoading()) newsArticlesCrudMethods.getNewsArticle({ id, includeContent: true });
          return true;
        },
        goToSelectedNewsArticleCommunityActions: async (articleId: string, communityId: string): Promise<boolean> => {
          const result = await router.navigate(['marketing', 'news-articles', 'article', articleId, 'communities', communityId]);
          if (!result) return false;

          patchState(store, { selectedNewsArticleCommunityId: communityId });
          return true;
        },
      };

      return {
        ...newsArticlesCrudMethods,
        ...articleConfigMethods,
        ...articleCommentsMethods,
        ...articleCommunitiesMethods,
        ...articlePromotionMethods,
        ...articleFieldExpertsMethods,
        ...articleTopicsOrderMethods,
        ...utilMethods,
        ...navigationActions,
      };
    },
  ),

  withHooks({
    onInit(store) {
      const injector = inject(Injector);

      effect(() => {
        const groupId = store.selectedNewsArticle()?.groupId;
        if (!groupId) return;

        untracked(() => {
          const newsArticleGroupsStore = injector.get(NewsArticleGroupsStore);
          if (!newsArticleGroupsStore.isSelectedGroupLoading() && newsArticleGroupsStore.selectedGroup()?.id !== groupId) {
            newsArticleGroupsStore.getOne(groupId);
          }
        });
      });
    },
  }),
);

export interface NewsArticlesStoreType
  // activate, deactivate, setContent, updateWorkloadsForGroupArticles, updateMembershipLevelsForGroupArticles
  extends
    NewsArticleConfigurationHandlingStoreType,
    // getAllComments, deleteComment
    NewsArticleCommentsStoreType,
    // selectCommunity, addCommunities, updateCommunity, deleteCommunity, approveCommunity
    NewsArticleCommunityHandlingStoreType,
    // addPromotion, updatePromotion, deletePromotion
    NewsArticlePromotionHandlingStoreType,
    // addFieldExpertToGroupArticles, removeFieldExpertFromGroupArticles
    NewsArticleFieldExpertsStoreType,
    NewsArticleTopicsOrderStoreType {
  readonly toggle: Signal<'Active' | 'All'>;
  readonly newsArticles: Signal<NewsArticleListItem[]>;
  readonly selectedNewsArticle: Signal<NewsArticle | null>;
  readonly isLoading: Signal<boolean>;
  readonly isSelectedNewsArticleLoading: Signal<boolean>;
  readonly isNewsArticleCreateLoading: Signal<boolean>;

  readonly newsArticleComments: Signal<NewsArticleComment[]>;
  readonly isNewsArticleCommentsLoading: Signal<boolean>;

  readonly selectedNewsArticleCommunityId: Signal<string | null>;
  readonly selectedNewsArticleCommunity: Signal<NewsArticleCommunity | null>;

  readonly newsArticlesByCommunity: Signal<NewsArticleListItem[]>;
  readonly isNewsArticlesByCommunityLoading: Signal<boolean>;

  readonly selectedNewsArticleId: Signal<string | null>;
  readonly activeNewsArticles: Signal<NewsArticleListItem[]>;

  readonly topicsOrder: Signal<TopicOrder[]>;
  readonly isTopicsOrderLoading: Signal<boolean>;
  readonly isSetTopicsOrderLoading: Signal<boolean>;

  readonly getAllNewsArticles: (params?: GetNewsArticlesParams) => void;
  readonly getNewsArticle: (input: { id: string; includeContent: boolean }) => void;
  readonly addNewsArticle: (params: { newsArticle: CreateNewsArticleRequest; communityId?: string }) => void;
  readonly getAllNewsArticlesByCommunity: (params?: GetNewsArticlesParams) => void;
  readonly updateNewsArticle: (input: { id: string; newsArticle: UpdateNewsArticleRequest; oldFileId?: string | null }) => void;
  readonly activateFileAndUpdateNewsArticle: (input: {
    file: { newId: string; oldId: string | null };
    entity: { id: string; dto: UpdateNewsArticleRequest };
  }) => void;
  readonly deleteFile: (input: { id: string }) => void;
  readonly deleteNewsArticle: (id: string) => void;
  readonly changeNewsArticleGroup: (params: { articleId: string; groupId: string | null }) => void;
  readonly createLocalizedVersion: (params: { articleId: string; req: CreateLocalizedArticleRequest }) => void;
  readonly modifyGroupInfoForArticlesInGroup: (group: NewsArticleGroup) => void;
  readonly duplicateNewsArticle: (params: { articleId: string; req: NewsArticleCopyRequest; communityId?: string }) => void;

  readonly copyArticleExternalLink: (articleId: string) => void;
  readonly resetState: (state: Partial<NewsArticlesState>) => void;

  readonly goToNewsArticleActions: (id: string, communityId?: string) => Promise<boolean>;
  readonly goToSelectedNewsArticleCommunityActions: (articleId: string, communityId: string) => Promise<boolean>;
}

export type NewsArticlesStore = StateSignals<NewsArticlesState> & WritableStateSource<NewsArticlesState>;

export function sortNewsByDateTime(a: { createdDateTime: Date }, b: { createdDateTime: Date }) {
  return a.createdDateTime < b.createdDateTime ? 1 : -1;
}

export function getStatusParamFromToggle(toggle: 'Active' | 'All'): { status?: NewsArticleStatus } {
  return toggle === 'Active' ? { status: 'Active' } : {};
}

export function addDataToListItem(response: NewsArticleListItem, stateItem: NewsArticle | null): NewsArticle {
  return {
    ...response,
    content: stateItem ? stateItem.content : null,
    translationStatus: stateItem ? stateItem.translationStatus : null,
  };
}

export function findAndReplaceByIdWithSort(list: NewsArticleListItem[], item: NewsArticleListItem) {
  return sortAlphabetically(
    findAndReplace<NewsArticleListItem>(list, item, i => i.id === item.id),
    sortNewsByDateTime,
  );
}
